import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { apiClientJSON } from "../../../apis/apiClient";
import * as endpoints from "../../../configs/endpoints";
import { useShowToast } from "../../../hooks";
import useFleetsByPaymentConfig from "../../../hooks/useFleetsByPaymentConfig";
import { modifyCurrencyToSymbol } from "../../../utils/utils";
import ButtonWithLoader from "../../ButtonWithLoader";
import InputController from "../../inputController";
import DateTimePickerController from "../../inputController/DateTimePickerController";
import { FormInputDropdown } from "../../inputController/FormInputDropdown";
import SwitchController from "../../inputController/SwitchController";
import TextAreaController from "../../inputController/TextAreaController";

const parseAmount = (value) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const MAX_BONUS_TIERS = 6;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 650,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 12,
  p: 4,
  overflowY: "auto",
  maxHeight: "90vh",
};

const TopUpBonusCampaignModal = ({ open, onClose, program }) => {
  const showToast = useShowToast();
  const {
    fleetsByPaymentConfig,
    fleetCurrencies,
    loading: loadingFleets,
  } = useFleetsByPaymentConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedTierIds, setRemovedTierIds] = useState([]);
  const isEdit = !!program;

  const methods = useForm({
    defaultValues: {
      name: "",
      description: "",
      paymentConfigurationId: "",
      isActive: true,
      startDate: null,
      endDate: null,
      displayTitle: "",
      displayMessage: "",
      tiers: [
        {
          minAmount: "",
          bonusType: "fixed",
          bonusAmount: "",
          bonusPercentage: "",
          maxBonusAmount: "",
        },
      ],
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tiers",
  });

  const watchPaymentConfig = watch("paymentConfigurationId");
  const currency = fleetCurrencies[watchPaymentConfig];
  const currencySymbol = currency ? modifyCurrencyToSymbol(currency) : "?";

  useEffect(() => {
    if (open && program) {
      setRemovedTierIds([]);
      reset({
        name: program.name || "",
        description: program.description || "",
        paymentConfigurationId:
          program.organizationPaymentConfigurationId || "",
        isActive: program.isActive ?? true,
        startDate: program.startDate ? new Date(program.startDate) : null,
        endDate: program.endDate ? new Date(program.endDate) : null,
        displayTitle: program.rules?.[0]?.displayTitle || "",
        displayMessage: program.rules?.[0]?.displayMessage || "",
        tiers: program.rules?.map((rule) => ({
          id: rule.id,
          minAmount: rule.conditions?.min_amount_minor
            ? (rule.conditions.min_amount_minor / 100).toString()
            : "",
          bonusType: rule.rewardConfig?.percentage_of_trigger
            ? "percentage"
            : "fixed",
          bonusAmount: rule.rewardConfig?.fixed_amount_minor
            ? (rule.rewardConfig.fixed_amount_minor / 100).toString()
            : "",
          bonusPercentage:
            rule.rewardConfig?.percentage_of_trigger?.toString() || "",
          maxBonusAmount: rule.rewardConfig?.max_amount_minor
            ? (rule.rewardConfig.max_amount_minor / 100).toString()
            : "",
        })) || [
          {
            minAmount: "",
            bonusType: "fixed",
            bonusAmount: "",
            bonusPercentage: "",
            maxBonusAmount: "",
          },
        ],
      });
    } else if (open) {
      setRemovedTierIds([]);
      reset({
        name: "",
        description: "",
        paymentConfigurationId: "",
        isActive: true,
        startDate: null,
        endDate: null,
        displayTitle: "",
        displayMessage: "",
        tiers: [
          {
            minAmount: "",
            bonusType: "fixed",
            bonusAmount: "",
            bonusPercentage: "",
            maxBonusAmount: "",
          },
        ],
      });
    }
  }, [open, program, reset]);

  const paymentConfigOptions = useMemo(() => {
    return Object.values(fleetsByPaymentConfig)
      .filter((fleet) => fleet.payment_configuration_id)
      .map((fleet) => {
        const fleetCurrency = fleetCurrencies[fleet.payment_configuration_id];
        return {
          value: fleet.payment_configuration_id,
          label: fleetCurrency
            ? `${fleet.name} (${fleetCurrency})`
            : fleet.name,
        };
      });
  }, [fleetsByPaymentConfig, fleetCurrencies]);

  const onSubmit = async (data) => {
    if (!isEdit && !currency) {
      showToast("Unable to determine currency for selected fleet", "error");
      return;
    }

    for (const tier of data.tiers) {
      if (!tier.minAmount || parseAmount(tier.minAmount) <= 0) {
        showToast("All tiers must have a valid minimum amount", "error");
        return;
      }
      if (
        tier.bonusType === "fixed" &&
        (!tier.bonusAmount || parseAmount(tier.bonusAmount) <= 0)
      ) {
        showToast("Fixed bonus tiers must have a valid bonus amount", "error");
        return;
      }
      if (
        tier.bonusType === "percentage" &&
        (!tier.bonusPercentage || parseAmount(tier.bonusPercentage) <= 0)
      ) {
        showToast(
          "Percentage bonus tiers must have a valid percentage",
          "error"
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      if (isEdit) {
        await apiClientJSON.put(endpoints.updateRewardProgram(program.id), {
          update: {
            set: {
              name: data.name,
              description: data.description,
              is_active: data.isActive,
              start_datetime: data.startDate?.toISOString() || null,
              end_datetime: data.endDate?.toISOString() || null,
            },
          },
        });

        for (const tier of data.tiers) {
          const rulePayload = {
            trigger_type: "WALLET_TOPPED_UP",
            conditions: {
              min_amount_minor: Math.round(parseAmount(tier.minAmount) * 100),
            },
            reward_config:
              tier.bonusType === "fixed"
                ? {
                    fixed_amount_minor: Math.round(
                      parseAmount(tier.bonusAmount) * 100
                    ),
                  }
                : {
                    percentage_of_trigger: parseAmount(tier.bonusPercentage),
                    ...(tier.maxBonusAmount && {
                      max_amount_minor: Math.round(
                        parseAmount(tier.maxBonusAmount) * 100
                      ),
                    }),
                  },
            display_title: data.displayTitle || null,
            display_message: data.displayMessage || null,
            priority: data.tiers.indexOf(tier) + 1,
          };

          if (tier.id) {
            await apiClientJSON.put(endpoints.updateRewardRule(tier.id), {
              update: { set: rulePayload },
            });
          } else {
            await apiClientJSON.post(endpoints.createRewardRule(), {
              ...rulePayload,
              reward_program_id: program.id,
              name: `${data.name} - Tier ${data.tiers.indexOf(tier) + 1}`,
              reward_type: "WALLET_CREDIT",
            });
          }
        }

        for (const ruleId of removedTierIds) {
          await apiClientJSON.put(endpoints.updateRewardRule(ruleId), {
            update: { set: { is_archived: true } },
          });
        }

        showToast("Campaign updated successfully", "success");
      } else {
        const programResponse = await apiClientJSON.post(
          endpoints.createRewardProgram(),
          {
            name: data.name,
            description: data.description,
            payment_configuration_id: data.paymentConfigurationId,
            is_active: data.isActive,
            start_datetime: data.startDate?.toISOString() || null,
            end_datetime: data.endDate?.toISOString() || null,
          }
        );

        const programId = programResponse.data?.body?.program?.id;
        if (!programId) {
          showToast(
            "Failed to create campaign - no program ID returned",
            "error"
          );
          setIsSubmitting(false);
          return;
        }

        for (let i = 0; i < data.tiers.length; i++) {
          const tier = data.tiers[i];
          await apiClientJSON.post(endpoints.createRewardRule(), {
            reward_program_id: programId,
            name: `${data.name} - Tier ${i + 1}`,
            trigger_type: "WALLET_TOPPED_UP",
            reward_type: "WALLET_CREDIT",
            conditions: {
              min_amount_minor: Math.round(parseAmount(tier.minAmount) * 100),
            },
            reward_config:
              tier.bonusType === "fixed"
                ? {
                    fixed_amount_minor: Math.round(
                      parseAmount(tier.bonusAmount) * 100
                    ),
                  }
                : {
                    percentage_of_trigger: parseAmount(tier.bonusPercentage),
                    ...(tier.maxBonusAmount && {
                      max_amount_minor: Math.round(
                        parseAmount(tier.maxBonusAmount) * 100
                      ),
                    }),
                  },
            display_title: data.displayTitle || null,
            display_message: data.displayMessage || null,
            priority: i + 1,
          });
        }

        showToast("Campaign created successfully", "success");
      }

      onClose();
    } catch (error) {
      console.error("Failed to save campaign:", error);
      showToast("Failed to save campaign", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTier = () => {
    append({
      minAmount: "",
      bonusType: "fixed",
      bonusAmount: "",
      bonusPercentage: "",
      maxBonusAmount: "",
    });
  };

  const handleRemoveTier = (index) => {
    const tierValues = methods.getValues(`tiers.${index}`);
    if (tierValues?.id) {
      setRemovedTierIds((prev) => [...prev, tierValues.id]);
    }
    remove(index);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom={true}>
          {isEdit
            ? "Edit Top-Up Bonus Campaign"
            : "Create Top-Up Bonus Campaign"}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <InputController
                name="name"
                label="Campaign Name"
                control={control}
                rules={{ required: "Name is required" }}
              />

              <TextAreaController
                name="description"
                label="Description"
                control={control}
                rows={2}
              />

              {isEdit ? (
                <TextField
                  label="Fleet"
                  value={
                    fleetsByPaymentConfig[
                      program?.organizationPaymentConfigurationId
                    ]?.name ||
                    program?.organizationPaymentConfigurationId ||
                    "-"
                  }
                  disabled={true}
                  fullWidth={true}
                  size="small"
                  helperText="Fleet cannot be changed after creation"
                />
              ) : (
                <FormInputDropdown
                  name="paymentConfigurationId"
                  label="Fleet"
                  control={control}
                  options={paymentConfigOptions}
                  errors={errors}
                  rules={{ required: "Fleet is required" }}
                  disabled={loadingFleets}
                />
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <DateTimePickerController
                  name="startDate"
                  label="Start Date"
                  control={control}
                  styles={{ flex: 1, minWidth: "auto" }}
                />
                <DateTimePickerController
                  name="endDate"
                  label="End Date"
                  control={control}
                  styles={{ flex: 1, minWidth: "auto" }}
                />
              </Box>

              <SwitchController
                name="isActive"
                label="Active"
                control={control}
                styles={{ minWidth: "auto", marginTop: 0, marginBottom: 0 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Bonus Tiers
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addTier}
                  disabled={fields.length >= MAX_BONUS_TIERS}
                >
                  Add Tier
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Add up to {MAX_BONUS_TIERS} tiers with different minimum top-up
                amounts. Users will receive the bonus from the highest tier they
                qualify for.
              </Alert>

              {fields.length >= MAX_BONUS_TIERS && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Maximum number of tiers ({MAX_BONUS_TIERS}) reached. Remove a
                  tier to add a new one.
                </Alert>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Min Amount ({currencySymbol})</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Type</TableCell>
                    <TableCell>Bonus</TableCell>
                    <TableCell width={50}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => {
                    const tierBonusType = watch(`tiers.${index}.bonusType`);
                    return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <TextField
                            {...methods.register(`tiers.${index}.minAmount`, {
                              required: true,
                            })}
                            size="small"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            placeholder="0"
                            fullWidth={true}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth={true}>
                            <Select
                              {...methods.register(`tiers.${index}.bonusType`)}
                              defaultValue={field.bonusType || "fixed"}
                            >
                              <MenuItem value="fixed">Fixed</MenuItem>
                              <MenuItem value="percentage">Percentage</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {tierBonusType === "fixed" ? (
                            <TextField
                              {...methods.register(
                                `tiers.${index}.bonusAmount`,
                                {
                                  required: true,
                                  min: {
                                    value: 0.01,
                                    message: "Must be greater than 0",
                                  },
                                }
                              )}
                              size="small"
                              type="number"
                              inputProps={{ step: "0.01" }}
                              placeholder={`Bonus (${currencySymbol})`}
                              fullWidth={true}
                            />
                          ) : (
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <TextField
                                {...methods.register(
                                  `tiers.${index}.bonusPercentage`,
                                  {
                                    required: true,
                                    min: {
                                      value: 1,
                                      message: "Must be at least 1%",
                                    },
                                    max: {
                                      value: 100,
                                      message: "Cannot exceed 100%",
                                    },
                                  }
                                )}
                                size="small"
                                type="number"
                                placeholder="%"
                                sx={{ width: "50%" }}
                              />
                              <TextField
                                {...methods.register(
                                  `tiers.${index}.maxBonusAmount`,
                                  {
                                    min: {
                                      value: 0.01,
                                      message: "Must be greater than 0",
                                    },
                                  }
                                )}
                                size="small"
                                type="number"
                                inputProps={{ step: "0.01" }}
                                placeholder={`Max (${currencySymbol})`}
                                sx={{ width: "50%" }}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {fields.length > 1 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveTier(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold">
                Display Settings
              </Typography>

              <InputController
                name="displayTitle"
                label="Display Title (shown to user)"
                control={control}
              />

              <TextAreaController
                name="displayMessage"
                label="Display Message (shown to user)"
                control={control}
                rows={2}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button onClick={onClose}>Cancel</Button>
                <ButtonWithLoader
                  text={isEdit ? "Update" : "Create"}
                  isLoading={isSubmitting}
                />
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default TopUpBonusCampaignModal;
